
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const passwordSchema = z.object({
  currentPassword: z.string().min(6, "Password must be at least 6 characters"),
  newPassword: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string().min(6, "Password must be at least 6 characters")
}).refine(data => data.newPassword === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
});

const Settings = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isCancelledAtPeriodEnd, setIsCancelledAtPeriodEnd] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState<{
    subscribed: boolean;
    subscription_start?: string;
    subscription_end?: string;
  } | null>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [feedbackReason, setFeedbackReason] = useState("");
  const [feedbackDetails, setFeedbackDetails] = useState("");
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  const CANCELLATION_REASONS = [
    { value: "not_enough_content", label: "I couldn't find the topics I was looking for" },
    { value: "content_quality", label: "The content didn't meet my expectations" },
    { value: "no_value", label: "It wasn't worth the price for me" },
    { value: "found_alternative", label: "I'm using a different resource now" },
    { value: "other", label: "Something else" },
  ];
  
  const form = useForm<z.infer<typeof passwordSchema>>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: ""
    }
  });

  // Fetch subscription status
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user) return;
      try {
        const { data, error } = await supabase.functions.invoke("check-subscription");
        if (error) throw error;
        setSubscriptionData(data);
      } catch (err) {
        console.error("Error fetching subscription:", err);
      } finally {
        setIsLoadingSubscription(false);
      }
    };
    fetchSubscription();
  }, [user]);

  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    try {
      const { data, error } = await supabase.functions.invoke("cancel-subscription", {
        body: { immediate: false },
      });
      if (error) throw error;
      toast({
        title: "Subscription cancelled",
        description: data.message,
      });
      setIsCancelledAtPeriodEnd(true);
      setShowFeedbackDialog(true);
      // Refresh subscription data
      const { data: refreshed } = await supabase.functions.invoke("check-subscription");
      setSubscriptionData(refreshed);
    } catch (err: any) {
      toast({
        title: "Error cancelling subscription",
        description: err.message || "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const handleSubmitFeedback = async () => {
    setIsSubmittingFeedback(true);
    try {
      // Store feedback as a page event for analytics
      await supabase.from("page_events").insert({
        event_type: "subscription_cancellation_feedback",
        page_path: "/settings",
        user_id: user?.id,
        user_email: user?.email,
        extra_data: {
          reason: feedbackReason,
          details: feedbackDetails,
        },
      });
      toast({
        title: "Thank you for your feedback",
        description: "Your feedback helps us improve HelloML.",
      });
    } catch {
      // Silently fail - feedback is optional
    } finally {
      setIsSubmittingFeedback(false);
      setShowFeedbackDialog(false);
    }
  };

  if (!isLoading && !user) {
    navigate('/login', { state: { returnTo: '/settings' } });
    return null;
  }
  
  const handleUpdatePassword = async (values: z.infer<typeof passwordSchema>) => {
    try {
      setIsSubmitting(true);
      
      const { error } = await supabase.auth.updateUser({
        password: values.newPassword
      });
      
      if (error) throw error;
      
      toast({
        title: "Password updated",
        description: "Your password has been updated successfully.",
      });
      
      form.reset();
    } catch (error: any) {
      toast({
        title: "Error updating password",
        description: error.message || "An error occurred while updating your password.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleDeleteAccount = async () => {
    // In a real app, you'd want to confirm this action with a modal
    if (window.confirm("Are you sure you want to delete your account? This action cannot be undone.")) {
      try {
        setIsSubmitting(true);
        
        // In a real app, you'd call a Supabase edge function to delete the user account
        // For now, just show a toast message
        toast({
          title: "Account deletion requested",
          description: "Please contact support to complete account deletion.",
        });
      } catch (error: any) {
        toast({
          title: "Error",
          description: error.message || "An error occurred while processing your request.",
          variant: "destructive"
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="container max-w-5xl py-12">
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-3xl py-12">
      <h1 className="text-3xl font-bold mb-8">Account Settings</h1>
      
      <Tabs defaultValue="general" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="subscription">Subscription</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
        </TabsList>
        
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>Update your account information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={user?.email} disabled />
                <p className="text-sm text-muted-foreground">Your email cannot be changed</p>
              </div>
              
              <div className="pt-4">
                <Button variant="destructive" onClick={handleDeleteAccount} disabled={isSubmitting}>
                  {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing...</> : "Delete Account"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="subscription">
          <Card>
            <CardHeader>
              <CardTitle>Subscription</CardTitle>
              <CardDescription>Manage your HelloML Premium subscription</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoadingSubscription ? (
                <div className="flex items-center gap-2 py-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-muted-foreground">Loading subscription details...</span>
                </div>
              ) : subscriptionData?.subscribed ? (
                <div className="space-y-4">
                  <div className="rounded-lg border p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Status</span>
                      {isCancelledAtPeriodEnd ? (
                        <span className="text-sm font-medium text-destructive bg-destructive/10 px-2 py-1 rounded">Cancelled</span>
                      ) : (
                        <span className="text-sm font-medium text-green-600 bg-green-50 px-2 py-1 rounded">Active</span>
                      )}
                    </div>
                    {subscriptionData.subscription_start && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Started</span>
                        <span>{new Date(subscriptionData.subscription_start).toLocaleDateString()}</span>
                      </div>
                    )}
                    {subscriptionData.subscription_end && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{isCancelledAtPeriodEnd ? 'Expires' : 'Renews / Expires'}</span>
                        <span>{new Date(subscriptionData.subscription_end).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>

                  {isCancelledAtPeriodEnd ? (
                    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 text-destructive" />
                        <span className="font-medium text-destructive">Subscription Cancelled</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        You will <strong>not be billed again</strong>. Your access to premium content will remain active until{' '}
                        {subscriptionData.subscription_end
                          ? new Date(subscriptionData.subscription_end).toLocaleDateString()
                          : 'the end of your billing period'}
                        . After that date, your account will revert to the free plan.
                      </p>
                    </div>
                  ) : (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" disabled={isCancelling}>
                          {isCancelling ? (
                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cancelling...</>
                          ) : (
                            "Cancel Subscription"
                          )}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-destructive" />
                            Cancel Subscription
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            Your subscription will remain active until the end of the current billing period. <strong>You will not be charged again.</strong> After that, you'll lose access to premium content. Are you sure you want to cancel?
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleCancelSubscription}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Yes, Cancel
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}

                  {/* Cancellation Feedback Dialog */}
                  <Dialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>We're sorry to see you go</DialogTitle>
                        <DialogDescription>
                          Your subscription has been cancelled and you will not be billed again. Could you help us improve by sharing why you're leaving?
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-2">
                        <RadioGroup value={feedbackReason} onValueChange={setFeedbackReason}>
                          {CANCELLATION_REASONS.map((reason) => (
                            <div key={reason.value} className="flex items-center space-x-2">
                              <RadioGroupItem value={reason.value} id={reason.value} />
                              <Label htmlFor={reason.value} className="cursor-pointer">{reason.label}</Label>
                            </div>
                          ))}
                        </RadioGroup>
                        {feedbackReason === "other" && (
                          <Textarea
                            placeholder="Tell us more..."
                            value={feedbackDetails}
                            onChange={(e) => setFeedbackDetails(e.target.value)}
                            className="resize-none"
                            rows={3}
                          />
                        )}
                      </div>
                      <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="ghost" onClick={() => setShowFeedbackDialog(false)}>
                          Skip
                        </Button>
                        <Button onClick={handleSubmitFeedback} disabled={!feedbackReason || isSubmittingFeedback}>
                          {isSubmittingFeedback ? (
                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>
                          ) : (
                            "Submit Feedback"
                          )}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              ) : (
                <div className="text-center py-6 space-y-3">
                  <p className="text-muted-foreground">You don't have an active subscription.</p>
                  <Button asChild>
                    <a href="/get-premium">Get Premium</a>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle>Change Password</CardTitle>
              <CardDescription>Update your password to secure your account</CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleUpdatePassword)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="currentPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Current Password</FormLabel>
                        <FormControl>
                          <Input type="password" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="newPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>New Password</FormLabel>
                        <FormControl>
                          <Input type="password" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirm New Password</FormLabel>
                        <FormControl>
                          <Input type="password" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <Button type="submit" disabled={isSubmitting} className="mt-2">
                    {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating...</> : "Update Password"}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
